<template ref="batch-list-view">
  <el-col
    :span="24"
    id="batches"
    v-loading.body="(receiving || changingPage) && !navigating"
    element-loading-text="Fetching page...">
    <el-row type="flex">
      <el-col :span="24">
        <el-row type="flex" justify="start" align="middle" id="batches-header">
          <HeaderInfoText :colspan="9" title="BATCHES" :meta="meta"></HeaderInfoText>
          <el-col :span="6">
            <el-row type="flex" justify="center" v-if="meta.count > 20">
              <el-pagination
                layout="prev, pager, next, jumper"
                :total="meta.count"
                :page-size="20"
                :current-page.sync="currentPage"
                @current-change="(page) => changePage({ page, active: 'True' }, true)">
              </el-pagination>
            </el-row>
          </el-col>
          <el-col :span="9" class="h-padded right-aligned-col">
            <el-button plain @click="toggleFilters">
              {{ showFilters ? "Hide filters" : "Show filters" }}
            </el-button>
            <el-button :disabled="!hasCreatePermission" type="primary" icon="plus" @click="createNewModel"></el-button>
          </el-col>
        </el-row>
        <el-row type="flex" justify="center" align="middle" :gutter="20">
          <Filters
            :filters="filters"
            :filtersVisible="showFilters"
            :filterSize="4"
            :current-page.sync="currentPage"
            @applied="currentPage = 1"
            clearAction="clearBatchState"
            clearMetadataAction="resetBatchMeta"
            searchAction="fetchBatchesWithQuery"
            :autoApply="false">
            <template v-for="(f, i) in filters" slot-scope="props" :slot="`${f.type}${i}`">
              <component :key="i" :is="f.type" :properties="f.props" />
            </template>
          </Filters>
        </el-row>
        <el-row type="flex" justify="end" align="middle">
          <el-col :span="24">
            <el-table
              id="batches-table"
              :ref="`${endpoint}Table`"
              v-if="meta.count && !changingPage"
              :data="data"
              border
              style="width: 100%; z-index: 0;"
              :maxHeight="tableHeight">
              <el-table-column prop="name" label="Name" align="left" fit>
                <template slot-scope="scope">
                  <el-row type="flex">
                    <a class="link-mock" @click="editExistingModel(scope.row.id)">
                      {{ scope.row.name }}
                    </a>
                  </el-row>
                </template>
              </el-table-column>
              <el-table-column prop="production" label="Production" align="left" fit>
                <template slot-scope="scope">
                  {{ getProductionById(scope.row.production) ? getProductionById(scope.row.production).name : '' }}
                </template>
              </el-table-column>
              <el-table-column prop="company.name" label="Company" align="left" fit>
                <template slot-scope="scope">
                  {{ getCompanyById(scope.row.company) ? getCompanyById(scope.row.company).name : '' }}
                </template>
              </el-table-column>
              <el-table-column prop="active" label="Active" align="center" width="160">
                <template slot-scope="scope">
                  <el-tag :type="scope.row.active ? 'success' : 'danger'" close-transition>
                    {{ scope.row.active ? 'Active' : 'Inactive' }}
                  </el-tag>
                </template>
              </el-table-column>
            </el-table>
            <el-row
              v-if="!receiving && !meta.count"
              align="middle"
              justify="center"
              class="nodata-heading">
              No data available
            </el-row>
          </el-col>
        </el-row>
      </el-col>
    </el-row>
    <NewBatch
      :title="editMode.active ? hasCreatePermission ? `Edit Batch` : `View Batch` : 'New Batch'"
      :editMode="editMode"
      :hasCreatePermission="hasCreatePermission"
      @show="showNewBatch"
      @disableEditMode="disableEditMode">
    </NewBatch>
  </el-col>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { payload } from '@/components/RouteComponents/Batches/actionPayload'
  import store from '@/store/store'
  import HeaderInfoText from '@/components/HeaderInfoText'
  import Filters from '@/components/Filters.vue'
  import KeywordFilter from '@/components/FilterComponents/KeywordFilter'
  import SelectFilter from '@/components/FilterComponents/SelectFilter'
  import AutocompleteFilter from '@/components/FilterComponents/AutocompleteFilter'
  import NewBatch from '@/components/RouteComponents/Batches/NewBatch.vue'
  import { payload as productionActionPayload } from '@/components/RouteComponents/Productions/actionPayload'
  import { payload as seriesActionPayload } from '@/components/RouteComponents/Series/actionPayload'
  import { payload as setActionPayload } from '@/components/RouteComponents/Sets/actionPayload'
  import { payload as locationActionPayload } from '@/components/RouteComponents/Locations/actionPayload'
  import { payload as freefieldActionPayload } from '@/components/RouteComponents/Freefields/actionPayload'
  import { payload as taxcodeActionPayload } from '@/components/RouteComponents/Taxcodes/actionPayload'
  import { payload as insurancecodeActionPayload } from '@/components/RouteComponents/Insurances/actionPayload'
  import AutocompleteLocalSearch from '@/utils/helpers/Search/AutocompleteLocalSearch'
  import { systemViewMixins } from '@/Mixins/SystemViewMixins'
  import { DataLoader } from '@/utils/helpers/DataLoader/DataLoader'

  export default {
    components: {
      NewBatch,
      Filters,
      KeywordFilter,
      SelectFilter,
      AutocompleteFilter,
      HeaderInfoText
    },
    mixins: [ systemViewMixins ],
    data () {
      return {
        tableHeight: 1000,
        showFilters: true,
        editMode: { active: false, modelId: null },
        changingPage: false
      }
    },
    methods: {
      showNewBatch (id) {
        const batch = this.getBatchById(id)
        if (batch && batch.id) {
          try {
            this.editExistingModel(id)
          } catch (e) {
            console.log(e)
          }
        }
      },
      /**
       * Show or hide filters
       */
      toggleFilters () {
        !this.showFilters ? this.showFilters = true : this.showFilters = false
      },
      /**
       * Resets editMode data, resets form to initial state, and displays the
       * modal to create a new model
       */
      createNewModel () {
        this.editMode = Object.assign({}, { active: false, modelId: null })
        this.$store.dispatch('changeModalVisibility', { entityType: 'batch', visible: true })
      },
      /**
       * Handles editing an existing batch
       * @param modelId   The id of the account that is being edited
       */
      editExistingModel (modelId) {
        this.editMode = Object.assign({}, { active: true, modelId })
        this.$store.dispatch('changeModalVisibility', { entityType: 'batch', visible: true })
      },
      /**
       * Turns edit mode off
       */
      disableEditMode () {
        this.editMode = Object.assign({}, { active: false, modelId: null })
      },
      /**
       * Get default data if they are not in store
       */
      async getDefaultData () {
        let payloads = []
        const {
          getAllSets,
          getAllLocations,
          getAllSeries,
          getAllFreefields,
          getAllTaxcodes,
          getAllInsurances
        } = this.$store.getters

        if (!getAllSets.length) {
          payloads.push(setActionPayload)
        }
        if (!getAllLocations.length) {
          payloads.push(locationActionPayload)
        }
        if (!getAllSeries.length) {
          payloads.push(seriesActionPayload)
        }
        if (!getAllFreefields.length) {
          payloads.push(freefieldActionPayload)
        }
        if (!getAllTaxcodes.length) {
          payloads.push(taxcodeActionPayload)
        }
        if (!getAllInsurances.length) {
          payloads.push(insurancecodeActionPayload)
        }
        let dataLoader = new DataLoader(this.$store, payloads, true, 200)
        dataLoader.loadData()
      }
    },
    computed: {
      ...mapGetters({
        data: 'getBatches',
        getCompanyById: 'getCompanyById',
        getProductionById: 'getProductionById',
        getBatchById: 'getBatchById',
        productions: 'getAllProductions',
        meta: 'getBatchMetadata',
        receiving: 'getBatchFetchStatus',
        deleting: 'getBatchDeleteStatus',
        navigating: 'getNavigationStatus',
        userIsEditingForm: 'getIsUserEditingForm',
        routeLeaveConfirmation: 'getRouteLeaveConfirmation',
        modalVisibilityState: 'getModalVisibilityState',
        checkIfUserHasRequiredPermissions: 'checkIfUserHasRequiredPermissions'
      }),
      hasCreatePermission () {
        return this.checkIfUserHasRequiredPermissions(['entry.view_batch', 'entry.add_batch'])
      },
      filters () {
        return [
          {
            type: 'keywordFilter',
            props: {}
          },
          {
            type: 'selectFilter',
            props: {
              filterName: 'active',
              multi: false,
              placeholder: 'All',
              defaults: [ 'True' ],
              opts: [
                { label: 'All', value: ' ' },
                { label: 'Active', value: 'True' },
                { label: 'Inactive', value: 'False' }
              ]
            }
          },
          {
            type: 'autocompleteFilter',
            props: {
              remote: false,
              filterName: 'production',
              clearable: true,
              triggerOnFocus: true,
              placeholder: 'Production',
              options: () => new AutocompleteLocalSearch(this.productions, productionActionPayload).createOptions(),
              actionPayload: productionActionPayload
            }
          }
        ]
      }
    },
    /**
     * Pre-route enter logic.
     * @param to
     * @param from
     * @param next
     */
    async beforeRouteEnter (to, from, next) {
      let newPayload = Object.assign({}, payload, { query: { active: 'True' } })
      try {
        await store.dispatch('clearLocalState', { clearMutation: newPayload.clearMutation, clearCache: true })
        await store.dispatch('fetchWithQuery', newPayload)
      } catch (e) {
        console.log(e)
      } finally {
        next()
      }
    },
    created () {
      this.endpoint = payload.endpoint
      this.moduleName = payload.moduleName
      this.payload = payload
      this.generateForm()
    },
    mounted () {
      this.$nextTick = () => {
        const tableHeight = () => {
          const parentHeight = document.getElementById('batches').offsetHeight
          const headerHeight = document.getElementById('batches-header').offsetHeight
          return parentHeight - headerHeight
        }
        this.tableHeight = tableHeight()
      }
      this.getDefaultData()
    },
    async beforeRouteLeave (to, from, next) {
      if (this.modalVisibilityState.batch && this.modalVisibilityState.batch.visible && !this.routeLeaveConfirmation && this.userIsEditingForm) {
        try {
          return await this.$store.dispatch('setDestinationRoute', to)
            .then(() => this.$store.dispatch('changeModalVisibility', { entityType: 'changeLossModal', visible: true }))
        } catch (e) {
          console.log(e)
        }
      }
      try {
        this.$store.dispatch('changeModalVisibility', { entityType: 'batch', visible: false })
        await this.$store.dispatch('clearFilters')
        next()
      } catch (e) {
        console.log(e)
      } finally {
        next()
      }
    }
  }
</script>

<style scoped>
  #batches-header {
    border-bottom: 1px solid lightgray;
  }
  .h-padded {
    padding: 0 12px;
  }
  .right-aligned-col {
    text-align: right;
  }
</style>


// WEBPACK FOOTER //
// src/components/RouteComponents/Batches/Batches.vue